import React, { useState, useEffect, useCallback } from 'react';
import { S } from '../styles';
import { getTheme } from '../theme';
import { API, fmtDate, fmtPhone, fmtCurrency, daysAgoLabel, copyToClipboard, authHeaders } from '../utils/helpers';

function CustomersPage({ theme = 'dark', pendingCustomer = null, onPendingCustomerLoaded = null, currentUser = null }) {
  const T = getTheme(theme);
  const isDark = theme === 'dark';
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [history, setHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [expandedBike, setExpandedBike] = useState(null);
  const [copied, setCopied] = useState(false);
  const [shopProfile, setShopProfile] = useState({});
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  const [analyticsNotice, setAnalyticsNotice] = useState('');

  // An error reply (503 while a new shop has no snapshot yet) is still JSON, and
  // used to be stored as if it were the snapshot. The dashboard then called .map
  // on lists that were not there and the whole page went blank. Only a reply
  // with the lists the dashboard draws is kept; anything else becomes a notice.
  const loadAnalytics = useCallback(() => {
    setLoadingAnalytics(true);
    setAnalyticsNotice('');
    fetch(`${API}/api/customers/analytics`, { credentials: 'include', headers: authHeaders() })
      .then(r => r.json().then(data => ({ ok: r.ok, data })))
      .then(({ ok, data }) => {
        if (ok && Array.isArray(data?.topByVisits) && Array.isArray(data?.topBySpend)) {
          setAnalytics(data);
        } else {
          setAnalytics(null);
          setAnalyticsNotice(data?.detail || data?.error || 'Customer analytics are not available yet.');
        }
        setLoadingAnalytics(false);
      })
      .catch(() => { setAnalytics(null); setAnalyticsNotice('Customer analytics could not be loaded.'); setLoadingAnalytics(false); });
  }, []);

  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);

  // Auto-load customer history when navigated from CustomerPopover
  useEffect(() => {
    if (pendingCustomer?.id) {
      loadHistory(pendingCustomer);
      if (onPendingCustomerLoaded) onPendingCustomerLoaded();
    }
  }, [pendingCustomer]);

  // Debounced search
  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(`${API}/api/customers?q=${encodeURIComponent(query)}`, { credentials:'include', headers: authHeaders() });
        const data = await res.json();
        setResults(Array.isArray(data) ? data : []);
      } catch (_) { setResults([]); }
      setSearching(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [query]);

  const loadHistory = async (customer) => {
    setSelectedCustomer(customer);
    setHistory(null);
    setExpandedBike(null);
    setLoadingHistory(true);
    try {
      const res = await fetch(`${API}/api/customers/${customer.id}/history`, { credentials:'include', headers: authHeaders() });
      const data = await res.json();
      setHistory(data);
      // Auto-expand first bike
      if (data.bikes?.length > 0) setExpandedBike(data.bikes[0].id);
    } catch (_) {}
    setLoadingHistory(false);
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' }) : '—';
  const fmtCurrency = (n) => `$${parseFloat(n||0).toFixed(2)}`;
  const fmtPhone = (p) => { const n=(p||'').replace(/\D/g,''); if(n.length===10) return `(${n.slice(0,3)}) ${n.slice(3,6)}-${n.slice(6)}`; return p; };
  const daysAgoLabel = (d) => { if (d === 0) return 'Today'; if (d === 1) return 'Yesterday'; if (d < 30) return `${d}d ago`; if (d < 365) return `${Math.floor(d/30)}mo ago`; return `${Math.floor(d/365)}yr ago`; };

  useEffect(() => {
    fetch(`${API}/api/settings`, { credentials: 'include', headers: authHeaders() })
      .then(r => r.json())
      .then(s => { if (s.shopProfile) setShopProfile(s.shopProfile); })
      .catch(() => {});
  }, []);

  const buildHistoryEmail = () => {
    if (!history) return '';
    const c = history.customer;
    const lines = [];
    lines.push(`Hi ${c.firstName},`);
    lines.push('');
    lines.push(`Here is your service history at ${shopProfile?.name || 'our shop'}:`);
    lines.push('');
    history.bikes.filter(b => b.totalVisits > 0).forEach(bike => {
      lines.push(`🚲 ${bike.description}${bike.color ? ` (${bike.color})` : ''}`);
      lines.push(`   ${bike.totalVisits} service visit${bike.totalVisits !== 1 ? 's' : ''}`);
      bike.tickets.forEach(t => {
        lines.push(`   • ${fmtDate(t.timeIn)} — ${t.status}${t.note ? ': ' + t.note.split('\n')[0] : ''}`);
        if (t.saleLines && t.saleLines.length > 0) {
          t.saleLines.forEach(l => {
            const typeLabel = l.type === 'labour' ? 'Labor' : l.isInventory ? 'Part' : 'Misc';
            lines.push(`     - [${typeLabel}] ${l.description}${l.quantity > 1 ? ` ×${l.quantity}` : ''}: $${l.subtotal.toFixed(2)}`);
          });
          lines.push(`     Total: $${t.fullTotal?.toFixed(2)}`);
        }
      });
      lines.push('');
    });
    lines.push(`Total service visits: ${history.summary.totalVisits}`);
    lines.push('');
    lines.push('Thank you for being a valued customer!');
    lines.push(shopProfile?.signature || `${shopProfile?.name || ''} Service Team`);
    return lines.join('\n');
  };

  const sendHistory = () => {
    if (!history) return;
    const text = buildHistoryEmail();
    copyToClipboard(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const openEmailModal = () => {
    if (!history) return;
    setEmailSubject(`Your service history at ${shopProfile?.name || 'our shop'}`);
    setEmailBody(buildHistoryEmail());
    setEmailSent(false);
    setEmailError('');
    setShowEmailModal(true);
  };

  const sendHistoryEmail = async () => {
    if (!history?.customer?.email) return;
    setEmailSending(true);
    setEmailError('');
    try {
      const token = localStorage.getItem('sb_token');
      const res = await fetch(`${API}/api/contact-log/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          to: history.customer.email,
          subject: emailSubject,
          body: emailBody,
          customerId: history.customer.customerID || history.customer.id || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setEmailError(data.error || 'Failed to send. Please try again.'); }
      else { setEmailSent(true); setTimeout(() => setShowEmailModal(false), 2000); }
    } catch { setEmailError('Connection error. Please try again.'); }
    setEmailSending(false);
  };

  const fmtCurrencyShort = (n) => {
    if (n >= 1000) return `$${(n/1000).toFixed(1)}k`;
    return `$${parseFloat(n||0).toFixed(0)}`;
  };

  // Skeleton shimmer block
  const Skeleton = ({ w = '100%', h = 14, mb = 8, radius = 4 }) => (
    <div style={{ width: w, height: h, borderRadius: radius, marginBottom: mb,
      background: isDark
        ? 'linear-gradient(90deg, #1e2a40 25%, #243050 50%, #1e2a40 75%)'
        : 'linear-gradient(90deg, #e8e8e2 25%, #f0f0ea 50%, #e8e8e2 75%)',
      backgroundSize: '200% 100%',
      animation: 'shimmer 1.4s infinite',
    }} />
  );

  return (
    <div>
      <style>{`@keyframes shimmer { 0% { background-position: 200% 0 } 100% { background-position: -200% 0 } }`}</style>

      <div style={{ ...S.topbar, background:T.topbar }}>
        <div style={{ ...S.pageTitle, color:T.text }}>Customers</div>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:11, color:T.textSub }}>Last 90 days</span>
          {analytics?.snapshotDate && (
            <span style={{ fontSize:11, color:T.textSub }}>
              · Snapshot: {new Date(analytics.snapshotDate).toLocaleString('en-US', { month:'short', day:'numeric', year:'numeric', hour:'numeric', minute:'2-digit' })}
            </span>
          )}
        </div>
      </div>

      {/* ── Analytics Dashboard ── */}
      {loadingAnalytics ? (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
          {[...Array(4)].map((_, i) => (
            <div key={i} style={{ background:T.card, border:`1px solid ${T.cardBorder}`, borderRadius:10, padding:'18px 20px' }}>
              <Skeleton w="60%" h={10} mb={12} />
              <Skeleton w="40%" h={28} mb={8} />
              <Skeleton w="80%" h={10} />
            </div>
          ))}
        </div>
      ) : analytics && (
        <>
          {/* KPI row */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:16 }}>
            {[
              { label:'Unique Customers', value: analytics.uniqueCustomers, sub:'visited in 90 days', accent:'#3498db' },
              { label:'New This Month',   value: analytics.newThisMonth,    sub:'first-time visitors', accent:'#2ecc71' },
              { label:'Returning',        value: analytics.returning,       sub:'visited more than once', accent:'#F4622A' },
              { label:'Avg Spend / Visit',value: `$${parseFloat(analytics.avgSpendPerTicket||0).toFixed(0)}`, sub:`${analytics.totalTickets} total tickets`, accent:'#f1c40f' },
            ].map(({ label, value, sub, accent }) => (
              <div key={label} style={{ ...S.kpi(accent), background:T.kpiCard, border:`1px solid ${T.cardBorder}`, borderTop:`3px solid ${accent}` }}>
                <div style={{ ...S.kpiLabel, color:T.textSub }}>{label}</div>
                <div style={{ ...S.kpiValue(accent) }}>{value}</div>
                <div style={{ ...S.kpiSub, color:T.textSub }}>{sub}</div>
              </div>
            ))}
          </div>

          {/* Top lists */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:24 }}>
            {/* Top by visits */}
            <div style={{ background:T.card, border:`1px solid ${T.cardBorder}`, borderRadius:10, padding:'16px 20px' }}>
              <div style={{ ...S.sectionTitle, color:T.tableHead, marginBottom:14 }}>Most Visits — 90 Days</div>
              {analytics.topByVisits.map((c, i) => (
                <div key={c.id} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                  <div style={{ fontSize:11, color:T.textMuted, minWidth:16, textAlign:'right' }}>{i+1}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, color:T.text, fontWeight:500 }}>{c.name}</div>
                    <div style={{ fontSize:11, color:T.textSub }}>{fmtCurrencyShort(c.spend)} spent</div>
                  </div>
                  <div style={{ ...S.riskBar, width:60, background: isDark ? '#243050' : '#e0e0d8' }}>
                    <div style={{ ...S.riskFill(Math.round((c.visits / analytics.topByVisits[0].visits) * 100)), background:'#3498db' }} />
                  </div>
                  <div style={{ fontSize:12, fontWeight:600, color:'#3498db', minWidth:28, textAlign:'right' }}>{c.visits}x</div>
                </div>
              ))}
            </div>

            {/* Top by spend */}
            <div style={{ background:T.card, border:`1px solid ${T.cardBorder}`, borderRadius:10, padding:'16px 20px' }}>
              <div style={{ ...S.sectionTitle, color:T.tableHead, marginBottom:14 }}>Top Spenders — 90 Days</div>
              {analytics.topBySpend.map((c, i) => (
                <div key={c.id} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                  <div style={{ fontSize:11, color:T.textMuted, minWidth:16, textAlign:'right' }}>{i+1}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, color:T.text, fontWeight:500 }}>{c.name}</div>
                    <div style={{ fontSize:11, color:T.textSub }}>{c.visits} visit{c.visits !== 1 ? 's' : ''}</div>
                  </div>
                  <div style={{ ...S.riskBar, width:60, background: isDark ? '#243050' : '#e0e0d8' }}>
                    <div style={{ ...S.riskFill(Math.round((c.spend / analytics.topBySpend[0].spend) * 100)), background:'#2ecc71' }} />
                  </div>
                  <div style={{ fontSize:12, fontWeight:600, color:'#2ecc71', minWidth:48, textAlign:'right' }}>{fmtCurrencyShort(c.spend)}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      {!loadingAnalytics && !analytics && analyticsNotice && (
        <div style={{ background:T.card, border:`1px solid ${T.cardBorder}`, borderRadius:10, padding:'14px 18px', marginBottom:20, fontSize:13, color:T.textMuted }}>
          {analyticsNotice}
        </div>
      )}

      {/* ── Search + History ── */}
      <div style={{ display:'flex', gap:24 }}>
        {/* Left panel — search */}
        <div style={{ width:280, flexShrink:0 }}>
          {/* Search input */}
          <div style={{ position:'relative', marginBottom:16 }}>
            <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:T.searchIcon, fontSize:16, pointerEvents:'none' }}>⌕</span>
            <input type="text" placeholder="Search by name…" value={query}
              onChange={e => { setQuery(e.target.value); setSelectedCustomer(null); setHistory(null); }}
              style={{ width:'100%', background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'10px 12px 10px 36px', color:T.text, fontSize:14, fontFamily:"'Inter', sans-serif", outline:'none', boxSizing:'border-box' }}
            />
            {query && <button onClick={() => { setQuery(''); setResults([]); setSelectedCustomer(null); setHistory(null); }}
              style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'transparent', border:'none', color:T.textSub, cursor:'pointer', fontSize:18 }}>×</button>}
          </div>
          {searching && <div style={{ color:T.textSub, fontSize:13, padding:'8px 0' }}>Searching…</div>}
          {!searching && query.length >= 2 && results.length === 0 && <div style={{ color:T.textSub, fontSize:13, padding:'8px 0' }}>No customers found</div>}
          {results.map(c => (
            <div key={c.id} onClick={() => loadHistory(c)}
              style={{ padding:'12px 14px', borderRadius:8, marginBottom:6, cursor:'pointer',
                background: selectedCustomer?.id === c.id ? (theme==='light' ? '#e8f5e9' : '#1e2a1e') : T.card,
                border: selectedCustomer?.id === c.id ? '1px solid #2ecc71' : `1px solid ${T.cardBorder}`,
                transition:'all 0.15s' }}
              onMouseEnter={e => { if (selectedCustomer?.id !== c.id) e.currentTarget.style.background = T.rowHover; }}
              onMouseLeave={e => { if (selectedCustomer?.id !== c.id) e.currentTarget.style.background = T.card; }}
            >
              <div style={{ fontWeight:500, color:T.text, fontSize:14 }}>{c.name}</div>
              {c.phone && <div style={{ fontSize:12, color:T.textSub, marginTop:3 }}>{fmtPhone(c.phone)}</div>}
              {c.email && <div style={{ fontSize:11, color:T.textSub }}>{c.email}</div>}
            </div>
          ))}
          {!query && <div style={{ color:T.textSub, fontSize:13, lineHeight:1.7, marginTop:8 }}>Search for a customer by first or last name to view their complete service history.</div>}
        </div>

        {/* Right panel — customer history */}
        <div style={{ flex:1, overflowY:'auto', maxHeight:'60vh' }}>
          {!selectedCustomer && !loadingHistory && (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:200, color:T.textSub, fontSize:14 }}>
              Select a customer to view their history
            </div>
          )}

          {/* Skeleton loader while history loads */}
          {loadingHistory && (
            <div style={{ padding:'4px 0' }}>
              {/* Customer header skeleton */}
              <div style={{ background:T.card, border:`1px solid ${T.cardBorder}`, borderRadius:10, padding:'20px 24px', marginBottom:16 }}>
                <Skeleton w="40%" h={22} mb={10} radius={6} />
                <Skeleton w="25%" h={12} mb={16} />
                <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12 }}>
                  {[...Array(3)].map((_, i) => (
                    <div key={i} style={{ background: isDark ? '#243050' : '#f4f4ef', borderRadius:8, padding:'12px 14px' }}>
                      <Skeleton w="50%" h={10} mb={8} />
                      <Skeleton w="60%" h={18} mb={0} />
                    </div>
                  ))}
                </div>
              </div>
              {/* Bike cards skeleton */}
              {[...Array(2)].map((_, i) => (
                <div key={i} style={{ background:T.card, border:`1px solid ${T.cardBorder}`, borderRadius:10, padding:'16px 20px', marginBottom:12 }}>
                  <Skeleton w="35%" h={16} mb={10} />
                  <Skeleton w="20%" h={10} mb={14} />
                  <Skeleton w="100%" h={8} mb={6} />
                  <Skeleton w="85%" h={8} mb={6} />
                  <Skeleton w="70%" h={8} mb={0} />
                </div>
              ))}
            </div>
          )}

        {history && !loadingHistory && (
          <div>
            {/* Customer header */}
            <div style={{ background:T.card, border:`1px solid ${T.cardBorder}`, borderRadius:12, padding:'20px 24px', marginBottom:20 }}>
              <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:16 }}>
                <div>
                  <div style={{ fontFamily:'Arial, sans-serif', fontSize:22, fontWeight:700, color:T.text }}>{history.customer.name}</div>
                  <div style={{ fontSize:12, color:T.textSub, marginTop:4 }}>Customer since {fmtDate(history.customer.memberSince)}</div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  {history.customer.email && (
                    <button onClick={openEmailModal}
                      style={{ ...S.btn(true), fontSize:12, padding:'8px 16px', background:'#3498db', color:'#fff' }}>
                      ✉ Email history
                    </button>
                  )}
                  <button onClick={sendHistory}
                    style={{ ...S.btn(true), fontSize:12, padding:'8px 16px', background:T.btnPrimary, color:T.btnPrimaryText }}>
                    {copied ? '✓ Copied!' : '📋 Copy history'}
                  </button>
                </div>
                {showEmailModal && (
                  <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}
                    onClick={() => !emailSending && setShowEmailModal(false)}>
                    <div style={{ background: isDark ? '#1a2336' : '#ffffff', borderRadius:12, padding:24, width:'100%', maxWidth:560, border:`1px solid ${T.cardBorder}`, boxShadow:'0 20px 60px rgba(0,0,0,0.4)' }}
                      onClick={e => e.stopPropagation()}>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
                        <div style={{ fontSize:15, fontWeight:600, color:T.text }}>Email Service History</div>
                        <button onClick={() => setShowEmailModal(false)} style={{ background:'none', border:'none', color:T.textSub, fontSize:22, cursor:'pointer', padding:'0 4px' }}>×</button>
                      </div>
                      <div style={{ marginBottom:12 }}>
                        <div style={{ fontSize:11, color:T.textSub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4 }}>To</div>
                        <div style={{ fontSize:13, color:T.text, padding:'7px 12px', background:T.input, borderRadius:6, border:`1px solid ${T.inputBorder}` }}>{history.customer.email}</div>
                      </div>
                      <div style={{ marginBottom:12 }}>
                        <div style={{ fontSize:11, color:T.textSub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4 }}>Subject</div>
                        <input value={emailSubject} onChange={e => setEmailSubject(e.target.value)}
                          style={{ width:'100%', padding:'7px 12px', background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6, color:T.text, fontSize:13, fontFamily:'Arial, sans-serif', outline:'none', boxSizing:'border-box' }} />
                      </div>
                      <div style={{ marginBottom:16 }}>
                        <div style={{ fontSize:11, color:T.textSub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4 }}>Message</div>
                        <textarea value={emailBody} onChange={e => setEmailBody(e.target.value)} rows={12}
                          style={{ width:'100%', padding:'10px 12px', background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6, color:T.text, fontSize:12, fontFamily:'monospace', outline:'none', resize:'vertical', boxSizing:'border-box', lineHeight:1.6 }} />
                      </div>
                      {emailError && <div style={{ background:'rgba(231,76,60,0.1)', border:'1px solid #e74c3c', borderRadius:6, padding:'8px 12px', fontSize:13, color:'#e74c3c', marginBottom:12 }}>{emailError}</div>}
                      {emailSent
                        ? <div style={{ textAlign:'center', padding:'12px 0', fontSize:14, color:'#2ecc71', fontWeight:600 }}>✓ Email sent successfully!</div>
                        : <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                            <button onClick={() => setShowEmailModal(false)}
                              style={{ padding:'8px 16px', borderRadius:6, background:'transparent', border:`1px solid ${T.cardBorder}`, color:T.textSub, fontSize:13, cursor:'pointer', fontFamily:'Arial, sans-serif' }}>
                              Cancel
                            </button>
                            <button onClick={sendHistoryEmail} disabled={emailSending || !emailSubject.trim() || !emailBody.trim()}
                              style={{ padding:'8px 20px', borderRadius:6, background:emailSending ? '#666' : '#3498db', border:'none', color:'#fff', fontSize:13, fontWeight:600, cursor:emailSending ? 'not-allowed' : 'pointer', fontFamily:'Arial, sans-serif' }}>
                              {emailSending ? 'Sending...' : '✉ Send Email'}
                            </button>
                          </div>
                      }
                    </div>
                  </div>
                )}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(5, 1fr)', gap:12 }}>
                <div style={{ ...S.infoItem, background:T.infoItem }}>
                  <div style={{ ...S.infoLabel, color:T.textSub }}>Phone</div>
                  {history.customer.phone
                    ? <a href={`tel:${history.customer.phone}`} style={{ color:'#3498db', fontSize:13, textDecoration:'none', fontWeight:500 }}>{fmtPhone(history.customer.phone)}</a>
                    : <div style={{ color:T.textSub, fontStyle:'italic', fontSize:12 }}>None on file</div>}
                </div>
                <div style={{ ...S.infoItem, background:T.infoItem }}>
                  <div style={{ ...S.infoLabel, color:T.textSub }}>Email</div>
                  {history.customer.email
                    ? <div style={{ fontSize:12, color:'#3498db', wordBreak:'break-all' }}>{history.customer.email}</div>
                    : <div style={{ color:T.textSub, fontStyle:'italic', fontSize:12 }}>None on file</div>}
                </div>
                <div style={{ ...S.infoItem, background:T.infoItem, textAlign:'center' }}>
                  <div style={{ ...S.infoLabel, color:T.textSub }}>Total Visits</div>
                  <div style={{ fontFamily:'Arial, sans-serif', fontSize:24, fontWeight:700, color:T.text }}>{history.summary.totalVisits}</div>
                </div>
                <div style={{ ...S.infoItem, background:T.infoItem, textAlign:'center' }}>
                  <div style={{ ...S.infoLabel, color:T.textSub }}>Last Visit</div>
                  <div style={{ fontSize:13, color:T.text, fontWeight:500 }}>{fmtDate(history.summary.lastVisit)}</div>
                  <div style={{ fontSize:11, color:T.textSub }}>{history.summary.lastVisit ? `${Math.floor((Date.now() - new Date(history.summary.lastVisit)) / (1000*60*60*24))}d ago` : ''}</div>
                </div>
                <div style={{ ...S.infoItem, background:T.infoItem, textAlign:'center' }}>
                  <div style={{ ...S.infoLabel, color:T.textSub }}>Total Spend</div>
                  <div style={{ fontFamily:'Arial, sans-serif', fontSize:22, fontWeight:700, color:'#2ecc71' }}>{fmtCurrency(history.summary.totalSpend)}</div>
                  <div style={{ fontSize:11, color:T.textSub }}>labor & parts</div>
                </div>
              </div>
            </div>

            {/* Bikes */}
            {history.bikes.filter(bike => bike.totalVisits > 0).length === 0
              ? <div style={{ color:T.textSub, fontSize:13, padding:20 }}>No service visits on file for this customer.</div>
              : history.bikes.filter(bike => bike.totalVisits > 0).map(bike => (
                <div key={bike.id} style={{ background:T.card, border:`1px solid ${T.cardBorder}`, borderRadius:12, marginBottom:12, overflow:'hidden' }}>
                  {/* Bike header */}
                  <div onClick={() => setExpandedBike(expandedBike === bike.id ? null : bike.id)}
                    style={{ padding:'16px 20px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between',
                      background: expandedBike === bike.id ? T.rowHover : 'transparent' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:14 }}>
                      <div style={{ fontSize:22 }}>🚲</div>
                      <div>
                        <div style={{ fontWeight:600, color:T.text, fontSize:15 }}>{bike.description}</div>
                        <div style={{ fontSize:12, color:T.textSub, marginTop:2 }}>{bike.color && `${bike.color} · `}{bike.totalVisits} visit{bike.totalVisits !== 1 ? 's' : ''}{bike.lastService && ` · Last in ${fmtDate(bike.lastService)}`}{bike.notOwned && ' · not on this customer’s record in Lightspeed'}</div>
                      </div>
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:16 }}>
                      <span style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize:12, color:'#2ecc71' }}>{fmtCurrency(bike.totalSpend)}</span>
                      <span style={{ color:T.textSub, fontSize:14 }}>{expandedBike === bike.id ? '▲' : '▼'}</span>
                    </div>
                  </div>

                  {/* Ticket timeline */}
                  {expandedBike === bike.id && (
                    <div style={{ padding:'0 20px 16px', borderTop:`1px solid ${T.border}` }}>
                      {bike.tickets.length === 0
                        ? <div style={{ color:T.textSub, fontSize:13, padding:'16px 0' }}>No service records for this bike.</div>
                        : bike.tickets.map((t, i) => (
                          <div key={t.id} style={{ display:'flex', gap:16, paddingTop:16 }}>
                            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', width:20, flexShrink:0 }}>
                              <div style={{ width:10, height:10, borderRadius:'50%', background: i === 0 ? '#2ecc71' : T.border, border: i === 0 ? '2px solid #2ecc71' : `2px solid ${T.textSub}`, flexShrink:0 }} />
                              {i < bike.tickets.length - 1 && <div style={{ width:2, flex:1, background:T.border, marginTop:4 }} />}
                            </div>
                            <div style={{ flex:1, paddingBottom:16 }}>
                              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                                  <span style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize:11, color:T.textSub }}>{t.displayId}</span>
                                  <span style={S.statusPill(t.status)}>{t.status}</span>
                                </div>
                                <div style={{ textAlign:'right' }}>
                                  <div style={{ fontSize:12, color:T.textSub }}>{fmtDate(t.timeIn)}</div>
                                  <div style={{ fontSize:11, color:T.textMuted }}>{daysAgoLabel(t.daysAgo)}</div>
                                </div>
                              </div>
                              {t.note && (
                                <div style={{ marginBottom:4 }}>
                                  <div style={{ fontSize:10, color:T.textSub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:3 }}>Receipt Note</div>
                                  <div style={{ fontSize:13, color:T.text, lineHeight:1.5, whiteSpace:'pre-wrap' }}>{t.note}</div>
                                </div>
                              )}
                              {t.internalNote && (
                                <div style={{ marginBottom:4 }}>
                                  <div style={{ fontSize:10, color:T.textSub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:3 }}>Internal Note</div>
                                  <div style={{ fontSize:12, color:T.textSub, lineHeight:1.5, fontStyle:'italic' }}>{t.internalNote}</div>
                                </div>
                              )}
                              {t.saleLines && t.saleLines.length > 0 && (
                                <div style={{ marginTop:8, borderTop:`1px solid ${T.border}`, paddingTop:8 }}>
                                  {t.saleLines.map((line, li) => (
                                    <div key={li} style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:4, gap:8 }}>
                                      <div style={{ display:'flex', alignItems:'flex-start', gap:6, flex:1 }}>
                                        <span style={{ fontSize:10, padding:'1px 6px', borderRadius:3, fontWeight:600, marginTop:1,
                                          background: line.type==='labour' ? (theme==='light'?'#dbeafe':'#1a2a3a') : line.type==='misc' ? (theme==='light'?'#fef3e2':'#2a1a0a') : (theme==='light'?'#d1fae5':'#1a2a1a'),
                                          color: line.type==='labour' ? '#3498db' : line.type==='misc' ? '#e67e22' : '#2ecc71',
                                          whiteSpace:'nowrap', flexShrink:0 }}>
                                          {line.type==='labour' ? 'LABOR' : line.type==='misc' ? 'MISC' : 'PART'}
                                        </span>
                                        <div style={{ flex:1 }}>
                                          <span style={{ fontSize:12, color:T.text, whiteSpace:'pre-line' }}>{line.description}</span>
                                          {line.quantity > 1 && <span style={{ fontSize:11, color:T.textSub, marginLeft:6 }}>×{line.quantity}</span>}
                                          {line.discountPercent > 0 && <span style={{ fontSize:10, color:'#e67e22', marginLeft:6 }}>-{Math.round(line.discountPercent*100)}%</span>}
                                        </div>
                                      </div>
                                      <span style={{ fontSize:12, color:T.text, fontFamily:"'IBM Plex Mono', monospace", flexShrink:0 }}>${line.subtotal.toFixed(2)}</span>
                                    </div>
                                  ))}
                                  <div style={{ display:'flex', justifyContent:'space-between', marginTop:8, paddingTop:6, borderTop:`1px solid ${T.border}` }}>
                                    <span style={{ fontSize:11, color:T.textSub }}>Subtotal ${(t.fullTotal - t.saleTax).toFixed(2)} + Tax ${t.saleTax?.toFixed(2)}</span>
                                    <span style={{ fontSize:13, color:'#2ecc71', fontFamily:"'IBM Plex Mono', monospace", fontWeight:600 }}>${t.fullTotal?.toFixed(2)}</span>
                                  </div>
                                </div>
                              )}
                              <div style={{ display:'flex', alignItems:'center', gap:12, marginTop:6 }}>
                                <span style={{ fontSize:11, color:T.textSub }}>Tech: {t.tech}</span>
                                {!t.hasSaleData && t.serviceCharges > 0 && <span style={{ fontSize:11, color:T.textSub, fontFamily:"'IBM Plex Mono', monospace" }}>${t.serviceCharges.toFixed(2)} labor/misc</span>}
                              </div>
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  )}
                </div>
              ))
            }
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

// ── Settings Page ─────────────────────────────────────────────────────────────

export default CustomersPage;
